import { addMonths } from 'date-fns';
import React, { useRef } from 'react';
import { ButtonGroup } from 'react-bootstrap';
import Flatpickr from 'react-flatpickr';
import {
  cloneDate,
  getMonthName,
  isSameOrAfter,
  isSameOrBefore,
} from '../../utils/dateUtils';
import { toFlatpickrLocale } from '../form/flatpickrUtils';
import { useCurrentLocale } from '../../context/I18n';
import { useTranslation } from 'react-i18next';
import Button from '../button';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

type FlatpickrRef = InstanceType<typeof Flatpickr> & {
  flatpickr: FlatpickrInstance;
};

type Props = {
  minMonth?: Date;
  maxMonth?: Date;
  currentMonth: Date;
  onMonthChanged: (newValue: Date) => void;
};
const MonthNav: React.FC<React.PropsWithChildren<Props>> = ({
  currentMonth,
  minMonth,
  maxMonth,
  onMonthChanged,
}) => {
  const locale = toFlatpickrLocale(useCurrentLocale());
  const { t } = useTranslation();
  const flatpickrRef = useRef<FlatpickrRef | null>(null);

  const handleDatePickerClick = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open();
    }
  };

  const handleDateChange = (dates: Date[]) => {
    if (dates[0] && onMonthChanged) {
      onMonthChanged(dates[0]);
    }
  };

  return (
    <div>
      <ButtonGroup size="sm" className="bg-gray rounded p-1">
        <Button
          disabled={
            minMonth && isSameOrBefore(currentMonth, minMonth, { unit: 'day' })
          }
          label="←"
          variant="gray"
          onClick={() =>
            onMonthChanged &&
            onMonthChanged(addMonths(cloneDate(currentMonth), -1))
          }
        />
        <Button
          variant="white"
          size="sm"
          className="rounded"
          label={getMonthName(currentMonth)}
          onClick={handleDatePickerClick}
        />
        <Flatpickr
          ref={flatpickrRef}
          className="datepicker-input-hidden"
          options={{
            locale,
            dateFormat: t('flatpickr.dateFormat'),
            enableTime: false,
            clickOpens: false,
            minDate: minMonth,
            maxDate: maxMonth,
          }}
          value={currentMonth}
          onChange={handleDateChange}
        />
        <Button
          disabled={
            maxMonth && isSameOrAfter(currentMonth, maxMonth, { unit: 'day' })
          }
          label="→"
          variant="gray"
          onClick={() =>
            onMonthChanged &&
            onMonthChanged(addMonths(cloneDate(currentMonth), 1))
          }></Button>
      </ButtonGroup>
    </div>
  );
};

export default MonthNav;
